import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";

export type EncounterExitGuard = {
  /** Signed note but encounter still in-progress - offer finalize before leaving. */
  shouldPromptFinalize: boolean;
  /** Signed with pending co-signer - informational prompt only. */
  shouldPromptAwaitingCosign: boolean;
  onFinalize: () => Promise<void>;
  finalizeLoading?: boolean;
};

type ExitPrompt = "finalize" | "awaiting-cosign" | null;

interface EncounterExitContextValue {
  registerExitGuard: (guard: EncounterExitGuard | null) => void;
  requestExit: () => void;
}

const EncounterExitContext = createContext<EncounterExitContextValue | null>(null);

export function useEncounterExitGuard() {
  const ctx = useContext(EncounterExitContext);
  if (!ctx) {
    throw new Error("useEncounterExitGuard must be used within EncounterExitProvider");
  }
  return ctx;
}

export function EncounterExitProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const guardRef = useRef<EncounterExitGuard | null>(null);
  const [prompt, setPrompt] = useState<ExitPrompt>(null);
  const [finalizing, setFinalizing] = useState(false);

  const registerExitGuard = useCallback((guard: EncounterExitGuard | null) => {
    guardRef.current = guard;
  }, []);

  const leaveChart = useCallback(() => {
    setPrompt(null);
    navigate(-1);
  }, [navigate]);

  const requestExit = useCallback(() => {
    const guard = guardRef.current;
    if (guard?.shouldPromptFinalize) {
      setPrompt("finalize");
      return;
    }
    if (guard?.shouldPromptAwaitingCosign) {
      setPrompt("awaiting-cosign");
      return;
    }
    leaveChart();
  }, [leaveChart]);

  async function handleFinalizeAndExit() {
    const guard = guardRef.current;
    if (!guard) {
      leaveChart();
      return;
    }
    setFinalizing(true);
    try {
      await guard.onFinalize();
      leaveChart();
    } catch {
      /* page toast handles errors; keep dialog open */
    } finally {
      setFinalizing(false);
    }
  }

  const guard = guardRef.current;
  const loading = finalizing || !!guard?.finalizeLoading;

  return (
    <EncounterExitContext.Provider value={{ registerExitGuard, requestExit }}>
      {children}
      <ConfirmDialog
        open={prompt === "finalize"}
        onOpenChange={(open) => !open && setPrompt(null)}
        title="Finalize visit before leaving?"
        description="Your note is signed, but this encounter is still in progress. Finalize the visit to mark it complete and update the schedule."
        confirmLabel="Finalize & Exit"
        cancelLabel="Stay in chart"
        variant="default"
        onConfirm={() => void handleFinalizeAndExit()}
        loading={loading}
      />
      <ConfirmDialog
        open={prompt === "awaiting-cosign"}
        onOpenChange={(open) => !open && setPrompt(null)}
        title="Awaiting co-signature"
        description="The note is signed and locked, but a supervising co-sign is still pending. The visit will remain open until co-sign is complete."
        confirmLabel="Exit chart"
        cancelLabel="Stay in chart"
        variant="default"
        onConfirm={leaveChart}
      />
    </EncounterExitContext.Provider>
  );
}
